import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface EscalationRow {
  readonly id: string;
  readonly trigger: string;
  readonly level: number;
  readonly status: string;
  readonly assignedRole: string;
  readonly raisedAt: string;
}

export function NotificationsEscalationsPage(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<EscalationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = (): void => {
    apiRequest<{ items: EscalationRow[] }>('/notifications/escalations', { token })
      .then((r) => setRows(r.items))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const acknowledge = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/notifications/escalations/${id}/acknowledge`, { token, method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="escalation-dashboard">
      <h1>{t('notif.escalations.title')}</h1>
      <DataGrid<EscalationRow>
        caption="escalations-table"
        rows={rows}
        getRowId={(r) => r.id}
        columns={[
          { key: 'raised', header: t('notif.escalations.raised'), value: (r) => r.raisedAt, render: (r) => at(r.raisedAt) },
          { key: 'trigger', header: t('notif.escalations.trigger'), value: (r) => r.trigger },
          { key: 'level', header: t('notif.escalations.level'), value: (r) => r.level, numeric: true },
          { key: 'assigned', header: t('notif.escalations.assigned'), value: (r) => r.assignedRole },
          { key: 'status', header: t('notif.common.status'), value: (r) => r.status },
        ]}
        actionsHeader={t('notif.common.action')}
        actions={(r) => (
          r.status === 'OPEN' ? (
            <Button type="button" disabled={busyId === r.id} onClick={() => void acknowledge(r.id)} variant="secondary">
              {busyId === r.id ? t('notif.escalations.acknowledging') : t('notif.escalations.acknowledge')}
            </Button>
          ) : null
        )}
      />
    </main>
  );
}
